import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "./use-toast";
import { Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

import { trackPurchaseEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

export default function PaymentSuccess({ setSelectedTab }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Phases: 'verifying' -> 'generating' -> 'done' | 'error'
  const [phase, setPhase] = useState('verifying');
  const [generationStatus, setGenerationStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isVerifying = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const session_id = searchParams.get('session_id');
    if (!session_id) {
      trackPurchaseEvents.checkoutAbandoned();
      navigate('/');
      return;
    }

    const verifyPayment = async () => {
      if (isVerifying.current) return;
      isVerifying.current = true;

      try {
        const verifyResponse = await fetch(`${API_URL}/payment-success?session_id=${session_id}`);
        if (!verifyResponse.ok) {
          trackPurchaseEvents.paymentFailed("Verfy response not ok");
          throw new Error('Payment verification failed');
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { needsRegeneration, imageId, heigth, width, size } = await verifyResponse.json();
        if (needsRegeneration) {
          setPhase('generating');
          const response = await fetch(`${API_URL}/generate_final_store`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageId, heigth, width, size }),
          });

          if (!response.ok) {
            trackPurchaseEvents.paymentSuccessError(size, needsRegeneration, "gen/store error");
            throw new Error('Failed to generate and store image');
          }
        } else {
          const response = await fetch(`${API_URL}/store-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageId }),
          });

          if (!response.ok) {
            trackPurchaseEvents.paymentSuccessError(size, needsRegeneration, "store error");
            throw new Error('Failed to store image');
          }
        }
        trackPurchaseEvents.paymentSuccess(size, needsRegeneration);
        // Bump the unseen-maps counter so other tabs see a badge on the
        // "Your Maps" button. Same-tab clears it immediately on the
        // history-tab redirect below — that's intentional.
        try {
          const cur = parseInt(localStorage.getItem('unseenMaps') || '0', 10);
          const next = (Number.isNaN(cur) ? 0 : cur) + 1;
          localStorage.setItem('unseenMaps', String(next));
        } catch (_e) { /* quota / private mode — no-op */ }
        setPhase('done');

        toast({
          title: "Success",
          description: "Payment successful! Your map is available in Previous Maps.",
          className: "bg-green-300 text-black",
          duration: 6000,
        });

        // Brief pause so the user actually sees the success state.
        await new Promise(r => setTimeout(r, 1500));

        setSelectedTab("history");
        navigate('/dashboard?tab=history', { replace: true });
      } catch (error) {
        setPhase('error');
        setErrorMessage(error.message);
        toast({
          title: "Error",
          description: "Failed to process payment: " + error.message,
          className: "bg-red-300 text-black",
          duration: 10000,
        });
        await new Promise(r => setTimeout(r, 2500));
        navigate('/dashboard', { replace: true });
      } finally {
        isVerifying.current = false;
      }
    };

    verifyPayment();
    return () => {
      isVerifying.current = true;
    };
  }, [navigate, toast, setSelectedTab]);

  // Mirror the dashboard polling pattern: while in 'generating', poll the
  // server-side state every 2s so the progress bar reflects real backend
  // progress (calibration → topography → geometries → save).
  useEffect(() => {
    if (phase !== 'generating') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setGenerationStatus(data);
      } catch (_e) { /* transient — keep polling */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase]);

  const pct = Math.max(0, Math.min(100, generationStatus?.progress || 0));
  const label = generationStatus?.label || 'Starting…';

  return (
    <div className="w-full flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30">
        <div className="mb-6 flex justify-center">
          {phase === 'verifying' && (
            <div className="h-20 w-20 rounded-full bg-blue-50 flex items-center justify-center ring-4 ring-blue-100">
              <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
            </div>
          )}
          {phase === 'generating' && (
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center ring-4 ring-purple-100">
              <Sparkles className="h-10 w-10 text-purple-500 animate-pulse" />
            </div>
          )}
          {phase === 'done' && (
            <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center ring-4 ring-green-100">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
          )}
          {phase === 'error' && (
            <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center ring-4 ring-red-100">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
          )}
        </div>

        <h1 className="text-2xl font-bold mb-2 text-gray-900">
          {phase === 'verifying' && 'Confirming your payment'}
          {phase === 'generating' && 'Crafting your map'}
          {phase === 'done' && 'All set!'}
          {phase === 'error' && 'Something went wrong'}
        </h1>

        <p className="text-gray-600 mb-6">
          {phase === 'verifying' && 'Hold tight while we verify with our payment provider.'}
          {phase === 'generating' && 'Rendering at the resolution you paid for. This usually takes 1–3 minutes.'}
          {phase === 'done' && 'Your map is ready in Previous Maps. Redirecting…'}
          {phase === 'error' && (errorMessage || 'Please try again or contact support.')}
        </p>

        {phase === 'generating' && (
          <div className="space-y-2">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{label} ({pct}%)</p>
            <p className="text-xs text-gray-400 mt-4">
              You can safely close this tab — the render keeps going on the server and
              your map will appear in Previous Maps once it's done.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
